// Selection rules for a hike's (or recording's) feature / activity / custom tags,
// shared by the planner's Save Plan dialog and the recording editor through
// HikeTagPicker.vue, so the two pickers behave identically. Every function returns
// a new list rather than mutating, so callers can hand the result straight to a
// Vuex commit or a v-model update.

export interface HikeTagSelection {
  featureTags: string[];
  activityTags: string[];
  customTags: string[];
}

const isTag = (tag: unknown): tag is string => typeof tag === 'string' && tag.length > 0;

/** Catalog tags (features, activities) are slugs, so they match exactly. */
export function toggleCatalogTag(selected: string[], tag: string): string[] {
  if (!isTag(tag)) return selected;
  return selected.includes(tag) ? selected.filter((value) => value !== tag) : [...selected, tag];
}

/** Custom tags are stored lower-cased by the backend, so they match case-insensitively. */
export function isCustomTagSelected(selected: string[], tag: string): boolean {
  if (!isTag(tag)) return false;
  const key = tag.toLowerCase();
  return selected.some((value) => value.toLowerCase() === key);
}

export function toggleCustomTag(selected: string[], tag: string): string[] {
  if (!isTag(tag)) return selected;
  const key = tag.toLowerCase();
  return isCustomTagSelected(selected, tag)
    ? selected.filter((value) => value.toLowerCase() !== key)
    : [...selected, tag];
}

/** Typing a tag creates it AND selects it; an already-selected one is left as is. */
export function addCustomTag(selected: string[], raw: string): string[] {
  const tag = (raw ?? '').trim();
  if (!tag || isCustomTagSelected(selected, tag)) return selected;
  return [...selected, tag];
}

/**
 * Chips shown in the Tags group: the user's previously-created catalog plus any
 * freshly typed tags not yet in it, deduped case-insensitively, catalog first.
 */
export function customTagOptions(available: string[], selected: string[]): string[] {
  const seen = new Set<string>();
  const options: string[] = [];
  [...(available ?? []), ...(selected ?? [])].forEach((tag) => {
    if (!isTag(tag)) return;
    const key = tag.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      options.push(tag);
    }
  });
  return options;
}

/** "dog-friendly" -> "Dog Friendly". */
export function formatTag(tag: unknown): string {
  if (typeof tag !== 'string') return '';
  return tag
    .split('-')
    .filter((segment) => segment.length > 0)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

/**
 * A recording's saved tags as a selection. The GraphQL fields are nullable (old
 * apps' caches predate them), so a missing list reads as "none".
 */
export function tagSelectionFrom(source: {
  featureTags?: string[] | null;
  activityTags?: string[] | null;
  customTags?: string[] | null;
} | null | undefined): HikeTagSelection {
  const list = (value: unknown): string[] => (Array.isArray(value) ? value.filter(isTag) : []);
  return {
    featureTags: list(source?.featureTags),
    activityTags: list(source?.activityTags),
    customTags: list(source?.customTags),
  };
}
